<template>
  <div>
    <p v-if="loadError" role="alert">{{ loadError }}</p>
    <h1 class="mt-2 mb-4">{{ calculator.name }}</h1>
    <div class="mess-box" v-if="calculator.description" v-html="calculator.description"></div>
    <div class="wrapper-block p-4 flex flex-column justify-content-center">
      <!-- Входные параметры -->
      <div v-for="(item, index) in calculator.input_data" :key="index" class="mb-3">
        <label :for="`var-${index}`" class="mb-2 name-data">
          {{ item.name }} <span class="light inline">({{ item.unit }})</span>
        </label>
        <div class="flex flex-row gap-3">
          <InputNumber
            :id="`var-${index}`"
            v-model="item.value"
            class="flex-grow-1 flex"
            @input="updateValue(index, $event.value)"
            :min-fraction-digits="0"
            :max-fraction-digits="4"
            locale="en-US"
          />

        </div>
      </div>

      <p v-if="calculationError" role="alert" class="calculation-error">{{ calculationError }}</p>
      <!-- Выходные параметры -->
      <div>
        <div
          v-for="(item, index) in calculator.output_data"
          :key="index"
          :class="[
            'mb-3',
            'flex',
            isDesktop ? 'align-items-center' : '',
            'w-full',
            isDesktop ? 'flex-row' : 'flex-column',
            isDesktop ? 'justify-content-end' : 'justify-content-start',
          ]"
        >
          <div id="formula" :class="isDesktop ? 'mb-2' : 'formula-m'">
            {{ item.formatedFormula }}
          </div>
          <div :class="isDesktop ? 'data-unit' : 'data-unit-m'">{{ item.value === null ? '—' : item.value }}</div>
        </div>
      </div>
    </div>
    <div class="mt-4 flex flex-row justify-content-end gap-3">
      <button class="default" :disabled="!canExport" @click="visible = true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
        >
          <path
            d="M13.6934 12.0731C13.0179 12.0731 12.4134 12.314 11.9512 12.6915L5.61344 9.3582C5.65789 9.17346 5.69344 8.98872 5.69344 8.79595C5.69344 8.60318 5.65789 8.41844 5.61344 8.2337L11.8801 4.9325C12.3601 5.33411 12.9912 5.5831 13.6934 5.5831C15.169 5.5831 16.3601 4.5068 16.3601 3.17346C16.3601 1.84013 15.169 0.763824 13.6934 0.763824C12.2179 0.763824 11.0268 1.84013 11.0268 3.17346C11.0268 3.36623 11.0623 3.55097 11.1068 3.73571L4.84011 7.03692C4.36011 6.63531 3.729 6.38631 3.02677 6.38631C1.55122 6.38631 0.360107 7.46262 0.360107 8.79595C0.360107 10.1293 1.55122 11.2056 3.02677 11.2056C3.729 11.2056 4.36011 10.9566 4.84011 10.555L11.169 13.8964C11.1246 14.065 11.0979 14.2417 11.0979 14.4184C11.0979 15.7116 12.2623 16.7638 13.6934 16.7638C15.1246 16.7638 16.289 15.7116 16.289 14.4184C16.289 13.1253 15.1246 12.0731 13.6934 12.0731Z"
            fill="#6CB0FF"
          />
        </svg>
        Поделиться
      </button>
      <button class="default" :disabled="!canExport || !online" title="Выгрузка PDF требует подключения к интернету" @click="downloadPDF">
        <svg
        xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
        >
          <path
            d="M8.21952 3.43047C11.1595 3.43047 13.5529 5.8238 13.5529 8.7638C13.5529 11.7038 11.1595 14.0971 8.21952 14.0971C5.27952 14.0971 2.88619 11.7038 2.88619 8.7638C2.88619 5.8238 5.27952 3.43047 8.21952 3.43047ZM8.21952 2.09714C4.53952 2.09714 1.55286 5.0838 1.55286 8.7638C1.55286 12.4438 4.53952 15.4305 8.21952 15.4305C11.8995 15.4305 14.8862 12.4438 14.8862 8.7638C14.8862 5.0838 11.8995 2.09714 8.21952 2.09714ZM8.88619 8.7638V6.09714H7.55286V8.7638H5.55286L8.21952 11.4305L10.8862 8.7638H8.88619Z"
            fill="#6CB0FF"
          />
        </svg>
        Скачать (PDF)
      </button>
    </div>
    <!-- Диалоговое окно для ссылки -->
    <Dialog
      v-model:visible="visible"
      modal
      header="Скопируйте ссылку"
      :style="{ width: '50rem' }"
      :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    >
      <div class="flex flex-row justify-content-between gap-3">
        <div class="info-link w-full">
          {{ shareableLink }}
        </div>
      </div>
      <template #footer>
        <Button
          :label="copySuccess ? 'Скопировано' : 'Скопировать'"
          :severity="copySuccess ? 'secondary' : ''"
          @click="copyToClipboard"
          :disabled="copySuccess"
        />
      </template>
    </Dialog>

    <!-- Дополнительный контент -->
    <div class="flex flex-column justify-content-between flex-wrap gap-3 mt-6">
      <div
        class="block border-round bg-white p-4 w-full shadow-1 flex justify-content-center mt-4"
        v-if="calculator.image_url"
      >
        <Image
          :src="'https://api.labhub.online/' + calculator.image_url"
          alt="Иллюстрация методики" @error="calculator.image_url = ''"
          width="330"
          preview
        />
      </div>
      <!-- Проверяем наличие calculator.output_data[0] перед доступом к value -->
      <!-- <div
        class="block border-round bg-white p-4 w-full shadow-1 mt-3"
        v-if="calculator.output_data && calculator.output_data.length > 0 && dataIndicator"
      >
        <gauge-indicator
          :value="calculator.output_data[0].value"
          :indicators="dataIndicator"
        />
      </div> -->
    </div>

    <div class="mt-8" v-if="calculator.formula_description">
      <h3 class="line">Интерпретация и описания результата формулы:</h3>
      <span
        class="font-light block border-round bg-white p-4 w-full mt-2 shadow-1"
        v-html="calculator.formula_description"
      ></span>
    </div>
    <div class="mt-6" v-if="calculator.links">
      <h3 class="line">Ссылки и названия ресурсов:</h3>
      <span
        class="font-light block border-round bg-white p-4 w-full mt-2 shadow-1"
        v-html="calculator.links"
      ></span>
    </div>
  </div>
</template>

<script setup>
import {online} from "@/utils/connectivity";
const loadError=ref('');
import { ref, onMounted, defineProps, computed, watch, nextTick, onUnmounted } from "vue";
import http from "@/http";
import InputNumber from "primevue/inputnumber";
import { useRoute, useRouter } from "vue-router";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
// import GaugeIndicator from "@/components/GaugeIndicator.vue";
import Image from "primevue/image";
import { evaluate } from "mathjs";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});

const calculator = ref({
  name: '',
  description: '',
  input_data: [],
  output_data: [],
  image_url: '',
  formula_description: '',
  links: '',
});
const calculationError = ref('');
const canExport = computed(() => calculator.value.output_data.length > 0 && !calculationError.value && calculator.value.output_data.every(output => typeof output.value === 'number' && Number.isFinite(output.value)));
const visible = ref(false);
const route = useRoute();
const router = useRouter();

const dataIndicator = ref([]);
const isDesktop = ref(window.innerWidth >= 1024);

onMounted(async () => {
  window.addEventListener('resize', handleResize);
  handleResize();

  try {
    const response = await http.get(`tools/tools/${props.id}`);
    calculator.value = response.data;
    dataIndicator.value = calculator.value.indicators || [];

    // Инициализируем значения input_data
    calculator.value.input_data.forEach((element) => {
      element.value = null; // Пустое поле не является измеренным нулём
    });

    // Получаем параметры запроса из URL
    const queryParams = route.query;

    calculator.value.input_data.forEach((element, index) => {
      if (queryParams[`var${index}`] !== undefined) {
        element.value = parseFloat(queryParams[`var${index}`].replace(",", "."));
      }
    });

    // Инициализируем формулы выходных параметров для отображения
    calculator.value.output_data.forEach((element) => {
      element.value = null;
      const displayFormula = element.showCustomFormula && element.customFormula ? element.customFormula : element.formula;
      element.formatedFormula = `$$${wrapRussianText(element.output_name)}(${wrapRussianText(element.output_unit)})=${wrapRussianText(displayFormula)}$$`;
    });

    nextTick(() => {
      updateValue();
      updateMathJax();
    });
  } catch (error) {
    loadError.value='Не удалось открыть калькулятор. Подключитесь к сети и сохраните инструмент для работы офлайн.';
  }
});

const handleResize = () => {
  isDesktop.value = window.innerWidth >= 1024;
};

const copySuccess = ref(false);

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(shareableLink.value);
    copySuccess.value = true;
    setTimeout(() => (copySuccess.value = false), 3000);
  } catch (error) {
    console.error("Ошибка копирования в буфер обмена", error);
  }
};

const shareableLink = computed(() => {
  let queryParams = {};
  calculator.value.input_data.forEach((item, index) => {
    if (typeof item.value === 'number' && Number.isFinite(item.value)) {
      queryParams[`var${index}`] = item.value.toString().replace(".", ",");
    }
  });
  if (Object.keys(queryParams).length > 0) {
    return `${window.location.origin}${
      router.resolve({ path: route.path, query: queryParams }).href
    }`;
  }
  return "";
});

const updateValue = (index, newValue) => {
  if (index !== undefined && calculator.value.input_data[index]) {
    calculator.value.input_data[index].value = newValue;
  }
  calculationError.value = '';
  calculator.value.output_data.forEach(output => { output.value = null; });
  if (calculator.value.input_data.some(input => input.value === null || input.value === undefined || !Number.isFinite(input.value))) {
    calculationError.value = 'Заполните все входные показатели числовыми значениями.';
    return;
  }

  const context = {};

    // Собираем все значения входных параметров
    calculator.value.input_data.forEach((input) => {
    context[input.id] = input.value;
  });

  calculator.value.output_data.forEach((output) => {
    let formulaToUse = output.formula; // Всегда используем основную формулу для вычислений
    let formulaWithValues = formulaToUse;

    // Заменяем идентификаторы их значениями из контекста
    Object.keys(context).forEach((key) => {
      const value = context[key];
      const regex = new RegExp(`(?<![\\wа-яА-ЯёЁ])${key}(?![\\wа-яА-ЯёЁ])`, 'g');
      formulaWithValues = formulaWithValues.replace(regex, `(${value})`);
    });

    // Обновляем контекст, чтобы учитывать зависимости между выходными параметрами
    Object.keys(context).forEach((key) => {
      const value = context[key];
      const regex = new RegExp(`(?<![\\wа-яА-ЯёЁ])${key}(?![\\wа-яА-ЯёЁ])`, 'g');
      formulaWithValues = formulaWithValues.replace(regex, `(${value})`);
    });

    // Преобразуем формулу LaTeX в совместимую с math.js
    try {
      const mathJsFormula = latexToMathJS(formulaWithValues);
      const result = evaluate(mathJsFormula);
      if (typeof result !== 'number' || !Number.isFinite(result)) throw new Error('Non-finite result');
      output.value = parseFloat(result.toFixed(4));
      // Обновляем контекст новым выходным значением
      context[output.output_id] = output.value;
    } catch (error) {
      console.error("Ошибка вычисления формулы:", error);
      output.value = null;
      context[output.output_id] = NaN;
      calculationError.value = 'Расчёт невозможен. Проверьте исходные значения: деление на ноль и недопустимые значения не дают результата.';
    }
  });
};


const latexToMathJS = (latexFormula) => {
  // Заменяем \frac{a}{b} на (a)/(b)
  const fracRegex = /\\frac{([^{}]+)}{([^{}]+)}/g;
  let mathJsFormula = latexFormula.replace(fracRegex, "($1)/($2)");

  // Заменяем ^ на pow()
  const powerRegex = /(\{[^{}]+}|\d+(\.\d+)?|\w+)\^(\{[^{}]+}|\d+(\.\d+)?|\w+)/g;
  mathJsFormula = mathJsFormula.replace(powerRegex, "pow($1, $3)");


  // Заменяем функции
  mathJsFormula = mathJsFormula.replace(/\\sin/g, "sin");
  mathJsFormula = mathJsFormula.replace(/\\cos/g, "cos");
  mathJsFormula = mathJsFormula.replace(/\\tan/g, "tan");
  mathJsFormula = mathJsFormula.replace(/\\log/g, "log");

  // Удаляем специфический синтаксис LaTeX
  mathJsFormula = mathJsFormula.replace(/\\left/g, "");
  mathJsFormula = mathJsFormula.replace(/\\right/g, "");
  mathJsFormula = mathJsFormula.replace(/\\cdot/g, "*");

  return mathJsFormula;
};

const updateMathJax = () => {
  if (window.MathJax) {
    window.MathJax.typesetClear();
    window.MathJax.typesetPromise()
      .then(() => {})
      .catch((err) => console.error('Ошибка MathJax:', err));
  }
};

const downloadPDF = async () => {
  if(!online.value)return;
  if (!canExport.value) return;
  try {
    const response = await http.post(
      "tools/generate_pdf",
      calculator.value,
      { responseType: "blob" }
    );

    if (response.status === 200) {
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Калькулятор - ${calculator.value.name}.pdf`);

      link.click();
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error(error);
  }
};

const wrapRussianText = (formula) => {
  return formula.replace(/([а-яА-ЯёЁ\s]+)/g, '\\text{$1}');
};

watch(
  () => calculator.value.input_data.map(input => input.value),
  () => {
    updateValue();
    updateMathJax();
  },
  { deep: true, immediate: true } // Добавлено immediate: true для первоначального запуска
);


onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.wrapper-block {
  width: 100%;
  min-height: 80px;
  border-radius: 5px;
  background: var(--design-color-white, #fff);
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
}

.data-unit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  border: 1px solid #e5f0ff;
  background: #fafcff;
  height: 48px;
  flex-shrink: 0;
  padding-left: 12px;
  padding-right: 12px;
  min-width: 120px;
  margin-left: 10px;
}

.formula-m{
  display: inline-flex;
  align-items: center;
  justify-content: left;
}

.data-unit-m {
  display: inline-flex;
  align-items: center;
  justify-content: left;
  border-radius: 5px;
  border: 1px solid #e5f0ff;
  background: #fafcff;
  height: 48px;
  flex-shrink: 0;
  padding-left: 12px;
  padding-right: 12px;
  width: 100%;
}

.name-data {
  font-weight: 500;
}

#formula {
  color: var(--medical-dark, #193B68);
}

/* Адаптивная настройка вёрстки */
@media (max-width: 1023px) {
  .flex-row {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
  .data-unit {
    width: 100% !important;
  }
  #formula {
    margin-bottom: 8px;
  }
}
</style>

<style scoped>
.calculation-error { padding: 12px 16px; background: #fff3f1; color: #912f26; border-left: 3px solid #bf4d40; }
button:disabled { opacity: .45; cursor: not-allowed; }
</style>
